import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import BrushIcon from "@mui/icons-material/Brush";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StopIcon from "@mui/icons-material/Stop";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import api from "../../api";
import VideoExplanationPlayer from "../../components/VideoExplanationPlayer";
import { ConfirmGenerate, POLL_MS, StateChip, VerificationChip, ago, canVerifyVideos, money, moneyAuto } from "./shared";
import VideoVerifyDialog from "./VideoVerifyDialog";

/** Where a running video's job is: waiting for a worker, or being made. */
function jobLabel(job) {
  if (!job) return "Running";
  const secs = (Date.now() - new Date(job.since).getTime()) / 1000;
  const retry = job.attempts > 1 ? ` (try ${job.attempts})` : "";
  return job.status === "working"
    ? `Being made for ${ago(secs)}${retry} on ${job.queue}`
    : `Waiting in the ${job.queue} queue for ${ago(secs)}${retry}`;
}

const FILTERS = [
  { key: "all", label: "All", states: null },
  { key: "missing", label: "Missing", states: ["missing"] },
  { key: "retry", label: "Failed / held", states: ["failed", "needs_review"] },
  { key: "flagged", label: "Flagged", states: ["flagged"] },
  { key: "completed", label: "Done", states: ["completed"] },
  { key: "awaiting", label: "Awaiting verification", states: ["completed"], verification: null },
  { key: "rejected", label: "Rejected", states: ["completed", "flagged"], verification: "rejected" },
  { key: "unpublished", label: "Verified, not published", states: ["completed"], verification: "approved", unpublished: true },
  { key: "in_progress", label: "Running", states: ["in_progress"] },
];

function matches(filter, q) {
  if (filter.states && !filter.states.includes(q.state)) return false;
  if (filter.unpublished && q.published) return false;
  if (filter.verification !== undefined && (q.verification || null) !== filter.verification) return false;
  return true;
}

/** Why students and parents can or cannot see this question's video. */
function studentReason(q, visibility) {
  if (q.student_visible) return "Students and parents can watch this video.";
  if (q.state === "flagged") return "Flagged by the automatic check: hidden until an admin approves it.";
  if (q.state !== "completed") return "No finished video yet.";
  if (visibility === "none") return "Hidden: the setting hides all videos from students and parents.";
  if (q.verification === "rejected") return "Hidden: a tester rejected this video.";
  if (q.verification !== "approved") return "Hidden until a tester verifies it and an admin publishes it.";
  return "Verified — publish it to show students and parents.";
}

export default function SetQuestions({ set, visibility, onChanged, onSchedule }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const canVerify = canVerifyVideos();

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/explanation-videos/sets/${set.type}/${set.id}/questions`);
      setRows(res.data?.data || []);
    } catch (e) {
      setRows([]);
      toast.error(e.response?.data?.message || "Could not load this set's questions.");
    }
  }, [set.type, set.id]);

  useEffect(() => {
    load();
  }, [load, set.counts, set.published, visibility]);

  // Follow each question while its video is being made.
  const running = (rows || []).some((q) => q.state === "in_progress");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  // Drop selections that can no longer be generated.
  useEffect(() => {
    if (!rows) return;
    const ok = new Set(rows.filter((q) => q.generatable).map((q) => q.id));
    setSelected((sel) => sel.filter((id) => ok.has(id)));
  }, [rows]);

  const afterChange = async () => {
    await load();
    onChanged();
  };

  const qaAction = async (question, action) => {
    setBusyId(question.id);
    try {
      const res = await api.post(`/admin/explanation-videos/questions/${set.type}/${question.id}/${action}`);
      toast.success(res.data?.message || "Done");
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update the video.");
    } finally {
      setBusyId(null);
    }
  };

  const publish = async (questionIds, on) => {
    setBusyId(questionIds.length === 1 ? questionIds[0] : "bulk");
    try {
      const res = await api.post("/admin/explanation-videos/bulk/publish", {
        items: [{ type: set.type, id: set.id, question_ids: questionIds }],
        publish: on,
      });
      toast.success(res.data?.message || "Saved.");
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not change who sees the video.");
    } finally {
      setBusyId(null);
    }
  };

  const askGenerate = (questions) => {
    const redo = questions.some((q) => q.state !== "missing");
    setConfirm({
      title:
        questions.length === 1
          ? `Q${questions[0].number}: ${redo ? "redo video" : "generate video"}`
          : "Generate selected videos",
      name: set.name,
      count: questions.length,
      cost: questions.reduce((sum, q) => sum + (q.estimated_cost_usd || 0), 0),
      note: redo
        ? "Failed, held, flagged and finished videos get a new script from the AI, and need publishing again."
        : null,
      ids: questions.map((q) => q.id),
    });
  };

  // Change style: the finished video is made again with its own script and voice; only the
  // presenter, board design or music change, so it costs (almost) nothing.
  const askRestyle = (q) =>
    setConfirm({
      title: `Q${q.number}: change style`,
      name: set.name,
      count: 1,
      cost: 0,
      note: "Pick a presenter, board design or music below. The script and voice stay the same. Students keep the current video until you publish the new one.",
      ids: [q.id],
      keepScript: true,
      styleOpen: true,
    });

  const runGenerate = async (style = {}) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/explanation-videos/sets/${set.type}/${set.id}/generate-questions`, {
        question_ids: confirm.ids,
        ...(confirm.keepScript ? { keep_script: true } : {}),
        ...(Object.keys(style).length ? { style } : {}),
      });
      toast.success(res.data?.message || "Videos queued.");
      setConfirm(null);
      setSelected([]);
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not queue the videos.");
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (questionIds) => {
    setBusyId(questionIds.length === 1 ? questionIds[0] : "bulk");
    try {
      const res = await api.post(`/admin/explanation-videos/sets/${set.type}/${set.id}/stop`, {
        question_ids: questionIds,
      });
      toast.success(res.data?.message || "Stopped.");
      await afterChange();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not stop the video.");
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null) {
    return <LinearProgress sx={{ my: 1 }} />;
  }

  const counts = Object.fromEntries(FILTERS.map((f) => [f.key, rows.filter((q) => matches(f, q)).length]));
  const active = FILTERS.find((f) => f.key === filter);
  const visible = rows.filter((q) => matches(active, q));
  const visibleIds = visible.filter((q) => q.generatable).map((q) => q.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selected.includes(id));
  const selectedRows = rows.filter((q) => selected.includes(q.id));
  const selectedCost = selectedRows.reduce((sum, q) => sum + (q.estimated_cost_usd || 0), 0);
  // Only videos a tester has approved can be published.
  const selectedDone = selectedRows.filter((q) => q.state === "completed" && q.verification === "approved" && (!q.published || q.pending_video_url));

  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const toggleAll = () =>
    setSelected((sel) =>
      allVisibleSelected ? sel.filter((id) => !visibleIds.includes(id)) : [...new Set([...sel, ...visibleIds])]
    );

  const scheduleSelected = () =>
    onSchedule({
      title: "Schedule selected videos",
      summary: `${selectedRows.length} question(s) of ${set.name}`,
      cost: selectedCost,
      items: [{ type: set.type, id: set.id, question_ids: selectedRows.map((q) => q.id) }],
      mode: "questions",
      onDone: () => setSelected([]),
    });

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
        {FILTERS.filter((f) => f.key === "all" || counts[f.key] > 0)
          .map((f) => (
            <Chip
              key={f.key}
              size="small"
              label={`${f.label} ${counts[f.key]}`}
              color={filter === f.key ? "primary" : "default"}
              variant={filter === f.key ? "filled" : "outlined"}
              onClick={() => setFilter(f.key)}
            />
          ))}
      </Stack>

      {selected.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            mb: 1,
            px: 2,
            py: 1,
            bgcolor: "#eef2ff",
            borderColor: "#c7d2fe",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {selected.length} selected · about {moneyAuto(selectedCost)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setSelected([])}>
            Clear
          </Button>
          {selectedDone.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<VisibilityIcon />}
              disabled={busyId === "bulk"}
              onClick={() => publish(selectedDone.map((q) => q.id), true)}
            >
              Publish {selectedDone.length}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<ScheduleIcon />} onClick={scheduleSelected}>
            Schedule…
          </Button>
          <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => askGenerate(selectedRows)}>
            Generate now
          </Button>
        </Paper>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                disabled={visibleIds.length === 0}
                onChange={toggleAll}
                inputProps={{ "aria-label": "Select all questions" }}
              />
            </TableCell>
            <TableCell width={48}>Q</TableCell>
            <TableCell>Question</TableCell>
            <TableCell width={130}>Video</TableCell>
            <TableCell width={110} align="center">
              Students
            </TableCell>
            <TableCell width={100}>Est. / spent</TableCell>
            <TableCell width={330} />
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((q) => {
            const reasons = [...(q.review_errors || []), ...(q.qa_notes ? q.qa_notes.split("\n") : [])];
            const busy = busyId === q.id;
            return (
              <TableRow key={q.id} hover selected={selected.includes(q.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.includes(q.id)}
                    disabled={!q.generatable}
                    onChange={() => toggle(q.id)}
                    inputProps={{ "aria-label": `Select question ${q.number}` }}
                  />
                </TableCell>
                <TableCell>{q.number}</TableCell>
                <TableCell>
                  <Typography variant="body2">{q.text || "—"}</Typography>
                  {reasons.length > 0 && (q.state === "flagged" || q.state === "needs_review") && (
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, color: "text.secondary" }}>
                      {reasons.slice(0, 4).map((r) => (
                        <Typography component="li" variant="caption" key={r}>
                          {r}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {q.verification && q.verified_by && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {q.verification === "approved" ? "Verified" : "Rejected"} by {q.verified_by}
                      {q.verified_at ? ` · ${new Date(q.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ""}
                    </Typography>
                  )}
                  {q.published && q.approved_by && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Published by {q.approved_by}
                      {q.approved_at ? ` · ${new Date(q.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ""}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={q.state === "in_progress" ? jobLabel(q.job) : ""}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {q.state === "in_progress" && q.job?.status === "waiting" ? (
                        <Chip size="small" color="default" variant="outlined" label="Queued" />
                      ) : (
                        <StateChip state={q.state} />
                      )}
                      {q.state === "in_progress" && q.job?.status !== "waiting" && <CircularProgress size={14} />}
                      {["completed", "flagged"].includes(q.state) && <VerificationChip question={q} />}
                    </Stack>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={studentReason(q, visibility)}>
                    {q.student_visible ? (
                      <Chip size="small" color="success" variant="outlined" icon={<VisibilityIcon />} label="Visible" />
                    ) : (
                      <Chip size="small" variant="outlined" icon={<VisibilityOffIcon />} label="Hidden" />
                    )}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      q.state === "missing"
                        ? "Estimated cost to make this video"
                        : "Estimated cost to make it again with a new script"
                    }
                  >
                    <Typography variant="body2" color={q.state === "missing" ? "text.primary" : "text.secondary"}>
                      {money(q.estimated_cost_usd, 3)}
                    </Typography>
                  </Tooltip>
                  {q.spent_usd > 0 && (
                    <Tooltip title="Real money spent on this question's videos so far, every attempt included">
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        Spent {money(q.spent_usd, 3)}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {q.video_url && <VideoExplanationPlayer allowDownload videoUrl={q.video_url} questionText={q.text} />}
                    {q.pending_video_url && (
                      <Tooltip title="A new version is ready. Students keep the current video until you publish this one.">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" color="info" variant="outlined" label="New version" />
                          <VideoExplanationPlayer allowDownload videoUrl={q.pending_video_url} questionText={q.text} />
                        </Stack>
                      </Tooltip>
                    )}
                    {canVerify && ["completed", "flagged"].includes(q.state) && (
                      <Tooltip title={q.pending_video_url ? "Watch the new version and approve or reject it" : "Watch the video and approve or reject it"}>
                        <Button size="small" variant={q.verification ? "text" : "outlined"} color="warning" disabled={busy} onClick={() => setVerifying(q)}>
                          {q.verification ? "Re-verify" : "Verify"}
                        </Button>
                      </Tooltip>
                    )}
                    {q.state === "completed" && (!q.published || q.pending_video_url) && (
                      <Tooltip title={q.verification === "approved" ? "" : "A tester must verify this video first"}>
                        <span>
                          <Button
                            size="small"
                            color="success"
                            disabled={busy || q.verification !== "approved"}
                            onClick={() => publish([q.id], true)}
                          >
                            {q.published ? "Publish new version" : "Publish"}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {q.state === "completed" && q.published && (
                      <Button size="small" color="inherit" disabled={busy} onClick={() => publish([q.id], false)}>
                        Unpublish
                      </Button>
                    )}
                    {q.state === "completed" && (
                      <Button size="small" disabled={busy} onClick={() => qaAction(q, "audit")}>
                        Check now
                      </Button>
                    )}
                    {canVerify && q.state === "flagged" && (
                      <Tooltip title="The video is fine: clear the flag and count it as verified. Publish it to show students.">
                        <Button size="small" color="success" disabled={busy} onClick={() => qaAction(q, "approve")}>
                          Approve
                        </Button>
                      </Tooltip>
                    )}
                    {q.state === "in_progress" && (
                      <Tooltip title="Take this video off the queue so it can be generated again">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<StopIcon />}
                          disabled={busy}
                          onClick={() => stop([q.id])}
                        >
                          Stop
                        </Button>
                      </Tooltip>
                    )}
                    {q.state === "missing" && (
                      <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => askGenerate([q])}>
                        Generate
                      </Button>
                    )}
                    {["failed", "needs_review", "flagged"].includes(q.state) && (
                      <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={() => askGenerate([q])}>
                        Retry
                      </Button>
                    )}
                    {q.state === "completed" && (
                      <Tooltip title="Change style: a new presenter, board design or music, same script and voice (almost free)">
                        <IconButton size="small" color="primary" onClick={() => askRestyle(q)} aria-label="Change style">
                          <BrushIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {q.state === "completed" && (
                      <Tooltip title="Make this video again with a new script">
                        <IconButton size="small" onClick={() => askGenerate([q])} aria-label="Redo video">
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
          {visible.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: "center" }}>
                  No questions in this view.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ConfirmGenerate confirm={confirm} submitting={submitting} onClose={() => setConfirm(null)} onConfirm={runGenerate} />
      <VideoVerifyDialog
        open={Boolean(verifying)}
        type={set.type}
        question={verifying}
        onClose={() => setVerifying(null)}
        onSaved={afterChange}
      />
    </Box>
  );
}
